import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Landmark, Zap, Leaf, TrendingUp, ShieldCheck, Clock, Sun } from "lucide-react";
import ScToolkitBack from "../../components/sc/ScToolkitBack";
import { BENEFIT_KEY_LABELS_EN } from "../../lib/flyerI18n";

const BENEFIT_ICONS = {
  zero_bill: Zap,
  clean_energy: Leaf,
  subsidy: Landmark,
  property_value: TrendingUp,
  low_maintenance: ShieldCheck,
  quick_payback: Clock,
};

const BENEFIT_DETAILS = {
  zero_bill: "Solar generates power right on the rooftop — the customer draws far less from the grid, so the monthly bill drops close to the minimum fixed charge.",
  clean_energy: "No fuel, no emissions. Every unit generated offsets fossil-fuel power drawn from the grid, for the full 25+ year life of the panels.",
  subsidy: "Central government subsidy under PM Surya Ghar Muft Bijli Yojana is credited directly to the customer's bank account after commissioning — it is not a discount we give, it's a government payout.",
  property_value: "A commissioned rooftop solar system with net-metering is a visible, transferable asset that adds resale value to any property.",
  low_maintenance: "No moving parts. Just a periodic panel wash and an annual health check — most systems run 20-25 years with minimal upkeep.",
  quick_payback: "With subsidy applied, most residential systems pay back their net cost in 4-5 years purely from bill savings — after that, electricity is free.",
};

const SUBSIDY_SLABS = [
  { kw: "Up to 1 kW", amount: 30000 },
  { kw: "Up to 2 kW", amount: 60000 },
  { kw: "3 kW & above", amount: 78000 },
];

export default function ScExplainers() {
  const [flyer, setFlyer] = useState(null);

  useEffect(() => {
    api.get("/sc/flyer-data").then(({ data }) => setFlyer(data)).catch(() => setFlyer({}));
  }, []);

  const benefitKeys = flyer?.benefit_keys?.length ? flyer.benefit_keys : Object.keys(BENEFIT_KEY_LABELS_EN);

  return (
    <div data-testid="sc-explainers" className="pb-6">
      <ScToolkitBack />
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Toolkit</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1 text-slate-900">Subsidy explainers.</h1>
      <p className="text-sm text-slate-500 mt-1.5">Quick, accurate talking points to answer customer questions in the field.</p>

      {/* PM Surya Ghar banner */}
      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)] flex items-center gap-3" data-testid="sc-explainer-scheme-banner">
        <div className="w-12 h-12 rounded-full border-2 border-emerald-300 flex items-center justify-center flex-shrink-0 bg-emerald-50">
          <Sun className="w-5 h-5 text-[#D97706]" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-900">PM Surya Ghar: Muft Bijli Yojana</div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            Central subsidy scheme for rooftop solar {flyer?.sc?.state ? `— ${flyer.sc.state} default slab shown below` : ""}
          </div>
        </div>
      </div>

      {/* Subsidy slabs */}
      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Subsidy slabs (residential)</div>
        <div className="grid grid-cols-3 gap-2.5" data-testid="sc-explainer-slabs">
          {SUBSIDY_SLABS.map((s) => (
            <div key={s.kw} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
              <div className="text-[10px] font-semibold text-slate-500 leading-tight">{s.kw}</div>
              <div className="font-mono text-base font-bold text-[#1B5E20] mt-1">₹{Number(s.amount).toLocaleString("en-IN")}</div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-slate-400 mt-2">Commercial installations are not eligible for this subsidy; Housing Societies follow a separate group-housing slab.</div>
      </div>

      {/* Benefit explainer cards */}
      <div className="mt-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Key benefits to explain</div>
        <div className="space-y-2.5" data-testid="sc-explainer-benefits">
          {benefitKeys.map((k) => {
            const Icon = BENEFIT_ICONS[k] || Zap;
            return (
              <div key={k} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)]" data-testid={`sc-explainer-${k}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#1B5E20]" />
                  </div>
                  <div className="text-sm font-bold text-slate-900">{BENEFIT_KEY_LABELS_EN[k] || k}</div>
                </div>
                <p className="text-[12.5px] text-slate-600 mt-2.5 leading-relaxed">{BENEFIT_DETAILS[k]}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
